import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";

const keyFor = (d) => `${d.getFullYear()}-${d.getMonth()}`; // YYYY-M

const capIT = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const formatLabel = (date) => {
  const monthName = date.toLocaleString("it-IT", { month: "long" });
  return `${date.getFullYear()} ${capIT(monthName)}`;
};

const generateRange = (anchorDate, fromOffset, toOffset) => {
  const out = [];
  for (let i = fromOffset; i <= toOffset; i++) {
    const d = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + i, 1);
    out.push({ value: d, label: formatLabel(d), key: keyFor(d) });
  }
  return out;
};

const MonthYearListbox = ({ selectedMonth, setSelectedMonth }) => {
  const [options, setOptions] = useState([]);
  const listRef = useRef(null);
  const PAGE = 10;

  // init
  useEffect(() => {
    const now = new Date();
    const seed = generateRange(now, -5, 5);
    setOptions(seed);
    if (!selectedMonth) setSelectedMonth(now);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // trova l'opzione selezionata (match per anno/mese)
  const selectedOption = useMemo(() => {
    if (!selectedMonth) return null;
    const k = keyFor(selectedMonth);
    return options.find((o) => o.key === k) || { value: selectedMonth, label: formatLabel(selectedMonth), key: k };
  }, [options, selectedMonth]);

  const mergeUnique = (base, extra, place = "end") => {
    const map = new Map(base.map((o) => [o.key, o]));
    for (const e of extra) if (!map.has(e.key)) map.set(e.key, e);
    const arr = Array.from(map.values());
    // Mantieni l’ordine temporale crescente
    return arr.sort((a, b) => a.value - b.value);
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    const atTop = el.scrollTop <= 0;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

    if (atTop && options.length) {
      const first = options[0].value;
      const more = generateRange(first, -PAGE, -1);
      setOptions((prev) => mergeUnique(prev, more, "start"));
      // mantieni posizione: piccolo offset per non “saltare”
      requestAnimationFrame(() => {
        if (listRef.current) listRef.current.scrollTop = 4;
      });
    } else if (atBottom && options.length) {
      const last = options[options.length - 1].value;
      const more = generateRange(last, 1, PAGE);
      setOptions((prev) => mergeUnique(prev, more, "end"));
    }
  };

  return (
    <div className="w-full">
      <Listbox value={selectedOption} onChange={(opt) => setSelectedMonth(opt.value)}>
        <div className="relative mt-1">
          <Listbox.Button className="relative h-10 w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left sm:text-sm shadow-md border border-gray-200">
            <span className="block truncate text-gray-700">
              {selectedOption ? selectedOption.label : "Seleziona mese"}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </span>
          </Listbox.Button>

          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Listbox.Options
              ref={listRef}
              onScroll={handleScroll}
              className="absolute mt-1 w-full max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-30"
            >
              {options.map((opt) => {
                const isSelected = selectedOption?.key === opt.key;
                return (
                  <Listbox.Option
                    key={opt.key}
                    value={opt}
                    className={({ active }) =>
                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                        active ? "bg-indigo-100 text-gray-900" : "text-gray-900"
                      }`
                    }
                  >
                    <>
                      <span className={`block truncate ${isSelected ? "font-medium" : "font-normal"}`}>{opt.label}</span>
                      {isSelected && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-800">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  </Listbox.Option>
                );
              })}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};

export default MonthYearListbox;